import TreeController from "~/controllers/tree_controller/tree_controller"
import { isHTMLElement, Maybe } from "~/types/types.util"
import attachTreeDrag, { DragSourceEvent, DropTargetEvent } from "./sorter/attach_tree_drag"
import { patch } from "@rails/request.js"
import { index } from "~/helpers/dom_helpers"
import { extractInstruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/list-item"

export default class Sorter {
  private controller: TreeController

  constructor(controller: TreeController) {
    this.controller = controller
  }

  // The node is a +li+ element
  initNode(node: Element) {
    if (!isHTMLElement(node)) return

    attachTreeDrag({
      element: node,
      onDragStart: this.#onDragStart.bind(this),
      // onDragEnd is called regardless of whether the drop was successful or not (i.e. not cancelled)
      onDragEnd: this.#onDragEnd.bind(this),
      // onDrop is only called when a valid drop is called
      onDrop: this.#onDrop.bind(this),
      onGenerateDragPreview: this.#onGenerateDragPreview.bind(this),
    })
  }

  #onGenerateDragPreview(evt: DragSourceEvent) {
    const node = evt.source.element
    // If node is expanded, we want to hide it before dragging and opening it again after drop in #onDrop
    if (this.controller.isOpened(node)) {
      this.controller.hide(node)
      node.dataset.expandAfterDrop = "true"
    }
  }

  #onDragStart(evt: DragSourceEvent) {
    evt.source.element.classList.add("opacity-25")
  }

  #onDragEnd(evt: DragSourceEvent) {
    const node = evt.source.element
    node.classList.remove("opacity-25")
    if (node.dataset.expandAfterDrop) {
      // If the node was expanded before dragging, we want to open it again after drop
      this.controller.show(node)
      delete node.dataset.expandAfterDrop
    }
  }

  #onDrop(evt: DropTargetEvent) {
    this.#patchPosition(evt)
    this.#rerenderAssociatedNodes(evt)
  }

  #rerenderAssociatedNodes({ self, location }: DropTargetEvent) {
    const fromNodes = location.initial.dropTargets
    const toNode = self.element
    if (fromNodes) {
      fromNodes.forEach((target) => this.controller.initNode(target.element))
    }
    if (toNode) {
      this.controller.initNode(toNode)
    }
  }

  #patchPosition(evt: DropTargetEvent) {
    const { source } = evt
    const { sorterUpdateUrl } = source.element.dataset
    if (!sorterUpdateUrl) return

    const newIndex = index(source.element)
    const parentId = this.#resolveParentId(evt)

    const params = {
      // If no parent is defined, we'd still like to send it to the backend to cover the case when a leaf becomes
      // a root
      [this.controller.parentParamNameValue]: parentId ?? null,
      [this.controller.indexParamNameValue]: newIndex + 1,
    }

    const body = this.controller.resourceNameValue
      ? {
          [this.controller.resourceNameValue]: params,
        }
      : params

    patch(sorterUpdateUrl, { body: JSON.stringify(body), responseKind: this.controller.responseKindValue })
  }

  #resolveParentId(evt: DropTargetEvent): Maybe<string> {
    const { self } = evt
    const toNode = self.element
    const instruction = extractInstruction(self.data)
    if (!isHTMLElement(toNode) || !instruction) return undefined

    return instruction.operation === "combine"
      ? toNode.dataset.nodeId
      : toNode.parentElement?.dataset.sorterParentId
  }
}
